import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AdminAppEngagement.css';

const FILTERS = [
  { id: 'all', label: 'All users' },
  { id: 'installed', label: '📲 App installed' },
  { id: 'push', label: '🔔 Notifications on' },
  { id: 'both', label: 'Both' },
  { id: 'neither', label: 'Neither' },
  { id: 'follows', label: '❤️ Follow someone' },
];

const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const osLabel = (os) => ({ ios: 'iPhone/iPad', android: 'Android', mac: 'Mac', windows: 'Windows', other: 'Desktop' }[os] || os);

const followCount = (u) => u.follows.dancers.length + u.follows.studios.length;

function Pill({ on, children }) {
  return <span className={`aae-pill ${on ? 'on' : 'off'}`}>{on ? children : '—'}</span>;
}

/**
 * Admin only (the page sits behind AdminRoute and the Cloud Function re-checks the caller):
 * which users installed the app, who has push notifications on, and who follows whom.
 */
export default function AdminAppEngagement() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [followedSearch, setFollowedSearch] = useState('');
  const [expandedFollowed, setExpandedFollowed] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await httpsCallable(getFunctions(), 'getAppEngagementReport')();
      setReport(res.data);
    } catch (e) {
      console.error(e);
      setError(e.code === 'functions/not-found'
        ? 'The report function is not deployed yet — deploy the latest Cloud Functions.'
        : e.message || 'Could not load the report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (setter, key) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const users = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.users
      .filter((u) => {
        if (filter === 'installed' && !u.install) return false;
        if (filter === 'push' && !u.push) return false;
        if (filter === 'both' && !(u.install && u.push)) return false;
        if (filter === 'neither' && (u.install || u.push)) return false;
        if (filter === 'follows' && followCount(u) === 0) return false;
        return !q || `${u.email || ''} ${u.name || ''}`.toLowerCase().includes(q);
      })
      .sort((a, b) => (b.lastSignInMs || 0) - (a.lastSignInMs || 0));
  }, [report, filter, search]);

  // dancer / studio → the users following it
  const followed = useMemo(() => {
    if (!report) return [];
    const map = new Map();
    report.users.forEach((u) => {
      const add = (type, name) => {
        const key = `${type}:${name}`;
        if (!map.has(key)) map.set(key, { key, type, name, followers: [] });
        map.get(key).followers.push(u);
      };
      u.follows.dancers.forEach((n) => add('dancer', n));
      u.follows.studios.forEach((n) => add('studio', n));
    });
    const q = followedSearch.trim().toLowerCase();
    return [...map.values()]
      .filter((f) => !q || f.name.toLowerCase().includes(q))
      .sort((a, b) => b.followers.length - a.followers.length || a.name.localeCompare(b.name));
  }, [report, followedSearch]);

  if (loading && !report) return <div className="loading-message">Loading users…</div>;
  if (error && !report) {
    return (
      <div className="stats-section">
        <p className="aae-error">{error}</p>
        <button type="button" className="aae-btn" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!report) return null;

  const t = report.totals;

  return (
    <div className="aae">
      <div className="stats-section">
        <div className="aae-head">
          <h2>📲 App installs &amp; notifications</h2>
          <button type="button" className="aae-btn" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
        <p className="aae-note">
          <strong>App installed</strong> means the user opened the installed app (Home Screen / app window)
          while signed in, or their browser reported an install. Installs are recorded from now on, so people who
          installed earlier appear after they next open the app. <strong>Notifications on</strong> means the user has
          at least one device registered for push; a device that was uninstalled or had its permission revoked can
          stay counted until a send to it fails.
        </p>
        {error && <p className="aae-error">{error}</p>}

        <div className="aae-cards">
          <div className="aae-card"><b>{t.users}</b><span>Registered users</span></div>
          <div className="aae-card"><b>{t.installed}</b><span>📲 App installed</span></div>
          <div className="aae-card"><b>{t.pushOn}</b><span>🔔 Notifications on</span></div>
          <div className="aae-card"><b>{t.emailOn}</b><span>✉️ Email alerts on</span></div>
          <div className="aae-card"><b>{t.following}</b><span>❤️ Follow someone</span></div>
        </div>
        <p className="aae-note">Updated {fmtDate(report.generatedMs)}</p>
      </div>

      {/* ── Users ───────────────────────────────────────────── */}
      <div className="stats-section">
        <h2>👤 Users ({users.length})</h2>
        <div className="aae-toolbar">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`aae-chip ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
          <input
            className="aae-search"
            type="search"
            placeholder="Search name or e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="aae-table-wrap">
          <table className="aae-table">
            <thead>
              <tr>
                <th>User</th>
                <th>App installed</th>
                <th>Notifications</th>
                <th>Email alerts</th>
                <th>Follows</th>
                <th>Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const open = expanded.has(u.uid);
                const n = followCount(u);
                return (
                  <React.Fragment key={u.uid}>
                    <tr>
                      <td data-label="User" className="aae-cell-user">
                        <div className="aae-user">{u.name || u.email || u.uid}</div>
                        {u.name && u.email && <div className="aae-sub">{u.email}</div>}
                      </td>
                      <td data-label="App installed"><Pill on={!!u.install}>✓ Yes</Pill></td>
                      <td data-label="Notifications"><Pill on={!!u.push}>✓ {u.push ? `${u.push.devices} device${u.push.devices === 1 ? '' : 's'}` : ''}</Pill></td>
                      <td data-label="Email alerts"><Pill on={u.emailAlerts}>✓ On</Pill></td>
                      <td data-label="Follows">
                        {n > 0 ? (
                          <button type="button" className="aae-expand" aria-expanded={open} onClick={() => toggle(setExpanded, u.uid)}>
                            {open ? '▼' : '▶'} {n} follow{n === 1 ? '' : 's'}
                          </button>
                        ) : '—'}
                      </td>
                      <td data-label="Last sign-in" className="aae-sub">{fmtDate(u.lastSignInMs)}</td>
                    </tr>
                    {open && (
                      <tr className="aae-detail">
                        <td colSpan={6} data-label="Follows">
                          {u.follows.dancers.length > 0 && (
                            <div className="aae-follow-group">
                              <strong>Dancers ({u.follows.dancers.length})</strong>
                              <div className="aae-tags">
                                {u.follows.dancers.map((name) => (
                                  <Link key={name} className="aae-tag" to={`/dancer/${encodeURIComponent(name)}`}>{name}</Link>
                                ))}
                              </div>
                            </div>
                          )}
                          {u.follows.studios.length > 0 && (
                            <div className="aae-follow-group">
                              <strong>Studios ({u.follows.studios.length})</strong>
                              <div className="aae-tags">
                                {u.follows.studios.map((name) => (
                                  <Link key={name} className="aae-tag" to={`/studio/${encodeURIComponent(name)}`}>{name}</Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={6} className="aae-empty">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Device details for installed users, kept compact under the table */}
        {filter === 'installed' && users.length > 0 && (
          <div className="aae-devices">
            <h3>Devices</h3>
            {users.map((u) => (
              <div key={u.uid} className="aae-device-row">
                <strong className="aae-device-user">{u.name || u.email}</strong>
                {u.install.devices.map((d, i) => (
                  <span key={i} className="aae-tag">
                    {osLabel(d.os)} · {d.browser}{d.launched ? '' : ' (installed, not opened yet)'} · {fmtDate(d.lastSeenMs)}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Who follows whom ─────────────────────────────────── */}
      <div className="stats-section">
        <h2>❤️ Who follows whom ({followed.length})</h2>
        <p className="aae-note">
          Users follow dancers and studios from their profiles. Click a row to see exactly which users follow it
          (visible to admins only).
        </p>
        <div className="aae-toolbar">
          <input
            className="aae-search"
            type="search"
            placeholder="Search dancer or studio…"
            value={followedSearch}
            onChange={(e) => setFollowedSearch(e.target.value)}
          />
        </div>
        <div className="aae-followed-list">
          {followed.map((f) => {
            const open = expandedFollowed.has(f.key);
            return (
              <div key={f.key} className={`aae-followed ${open ? 'open' : ''}`}>
                <button type="button" className="aae-followed-head" aria-expanded={open} onClick={() => toggle(setExpandedFollowed, f.key)}>
                  <span>{open ? '▼' : '▶'}</span>
                  <span className="aae-followed-name">{f.type === 'studio' ? '🏛️' : '💃'} {f.name}</span>
                  <span className="aae-followed-count">{f.followers.length} follower{f.followers.length === 1 ? '' : 's'}</span>
                </button>
                {open && (
                  <ul className="aae-followers">
                    {f.followers.map((u) => (
                      <li key={u.uid}>
                        {u.name || u.email || u.uid}
                        {u.name && u.email && <span className="aae-sub"> · {u.email}</span>}
                        {u.push && <span title="Notifications on"> 🔔</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {followed.length === 0 && <div className="aae-empty">No follows yet.</div>}
        </div>
      </div>
    </div>
  );
}
